import { Link } from "react-router-dom";
import {
  FiFacebook,
  FiInstagram,
  FiLinkedin,
  FiPhone,
  FiMail,
  FiClock,
} from "react-icons/fi";
import { FaXTwitter } from "react-icons/fa6";
import logoMark from "../../assets/home-page/header/logo-mark-glasses.svg";
import logoTextGenz from "../../assets/home-page/header/logo-text-genz.svg";
import logoTextPaisa from "../../assets/home-page/header/logo-text-paisa.svg";
import { siteConfig } from "../../config/site";

const QUICK_LINKS = [
  { label: "Home", href: "/" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "About Us", href: "/about-us" },
  { label: "Contact Us", href: "/contact-us" },
  { label: "FAQ", href: "/#faq" },
];

const LEGAL_LINKS = [
  { label: "Terms & Conditions", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Interest Rate Policy", href: "/interest-rate-policy" },
  { label: "Fair Practice Code", href: "/fair-practice-code" },
  { label: "Grievance Redressal", href: "/grievance-redressal" },
];

const POLICY_LINKS = [
  { label: "KYC & AML Policy", href: "/kyc-aml-policy" },
  { label: "Corporate Governance", href: "/corporate-governance" },
  { label: "Refund Policy", href: "/refund-policy" },
  { label: "Risk Management Policy", href: "/risk-management-policy" },
  { label: "Customer Data Policy", href: "/customer-data-policy" },
  { label: "Grievance Redressal Policy", href: "/grievance-redressal-policy" },
];

const SOCIAL_LINKS = [
  { icon: FiInstagram, label: "Instagram", href: siteConfig.social.instagram || "#" },
  { icon: FiFacebook, label: "Facebook", href: siteConfig.social.facebook || "#" },
  { icon: FaXTwitter, label: "X", href: siteConfig.social.twitter || "#" },
  { icon: FiLinkedin, label: "LinkedIn", href: siteConfig.social.linkedin || "#" },
];

const REGISTRATION_ITEMS = [
  siteConfig.rbiLicense ? `RBI Certificate: ${siteConfig.rbiLicense}` : null,
  siteConfig.bseScripCode ? `BSE Scrip Code: ${siteConfig.bseScripCode}` : null,
  siteConfig.cin ? `CIN: ${siteConfig.cin}` : null,
].filter((item): item is string => Boolean(item));

const LOCATIONS = [
  "Mumbai",
  "Pune",
  "Bangalore",
  "Chennai",
  "Hyderabad",
  "Delhi",
  "Gurgaon",
  "Noida",
  "Kolkata",
  "Ghaziabad",
  "Faridabad",
  "Jaipur",
  "Ahmedabad",
];

function FooterLinkColumn({
  heading,
  links,
}: {
  heading: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div className="flex flex-col gap-4">
      <h4 className="text-xs font-bold uppercase tracking-[0.5px] text-white">
        {heading}
      </h4>
      <ul className="flex flex-col gap-3">
        {links.map((link) => (
          <li key={link.label}>
            <Link
              to={link.href}
              className="text-sm text-[#c1add7] transition-colors hover:text-white"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="w-full rounded-t-[32px] bg-[#210d37]">
      <div className="mx-auto w-full max-w-[1440px] px-6 py-12 sm:px-10 sm:py-16 lg:px-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-8">
          {/* Brand info */}
          <div className="flex flex-col gap-5 lg:col-span-1">
            <div className="relative h-[40px] w-[80px]">
              <img
                src={logoMark}
                alt=""
                className="absolute left-0 top-[2px] h-[32px] w-[36px]"
              />
              <img
                src={logoTextGenz}
                alt=""
                className="absolute left-[40px] top-0 h-[14px] w-[44px] brightness-0 invert"
              />
              <img
                src={logoTextPaisa}
                alt="GenzPaisa"
                className="absolute left-[42px] top-[16px] h-[17px] w-[40px] brightness-0 invert"
              />
            </div>
            <p className="text-sm leading-5 text-[#c1add7]">
              Fast, Easy, and Paperless Personal Loans with instant approval
              and bank-grade security.
            </p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${social.label}`}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6d28d9]/40 text-white transition-colors hover:bg-[#6d28d9]/60"
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <FooterLinkColumn heading="Quick Links" links={QUICK_LINKS} />
          <FooterLinkColumn heading="Legal" links={LEGAL_LINKS} />
          <FooterLinkColumn heading="Policies" links={POLICY_LINKS} />

          {/* Reach us + verified badge */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.5px] text-white">
              Reach Us
            </h4>
            <div className="flex flex-col gap-3 text-sm text-[#c1add7]">
              {siteConfig.phoneHref ? (
                <a
                  href={siteConfig.phoneHref}
                  className="flex items-center gap-2 transition-colors hover:text-white"
                >
                  <FiPhone className="h-4 w-4 shrink-0" />
                  <span>{siteConfig.phoneDisplay}</span>
                </a>
              ) : null}
              {siteConfig.email ? (
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex items-center gap-2 transition-colors hover:text-white"
                >
                  <FiMail className="h-4 w-4 shrink-0" />
                  <span>{siteConfig.email}</span>
                </a>
              ) : null}
              <div className="flex items-center gap-2">
                <FiClock className="h-4 w-4 shrink-0" />
                <span>Mon–Fri, 10AM – 6PM</span>
              </div>
            </div>

            <div className="mt-1 flex flex-col gap-3 rounded-xl border border-solid border-[#6d3fa8]/50 bg-[#3b1766] p-4">
              <ul className="flex flex-col gap-2">
                {REGISTRATION_ITEMS.map((item) => (
                  <li
                    key={item}
                    className="break-words text-[11px] leading-4 text-[#c9b8de]"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Locations served */}
        <div className="mt-10 border-t border-solid border-white/10 pt-6">
          <p className="text-xs leading-5 text-[#a58bc4]">
            <span className="font-semibold text-white">
              Locations We Serve:{" "}
            </span>
            {LOCATIONS.join(" | ")}
          </p>
        </div>

        {/* Lower bar */}
        <div className="mt-6 flex flex-col gap-4 border-t border-solid border-white/10 pt-6 text-[11px] text-white/50 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-1 leading-4 lg:shrink-0">
            {siteConfig.nbfcName ? (
              <p className="font-semibold text-white/80 md:whitespace-nowrap">
                {siteConfig.nbfcName}
              </p>
            ) : null}
            {siteConfig.lspName ? (
              <p>
                Powered by{" "}
                <a
                  href={siteConfig.lspUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-white/30 underline-offset-2 transition-colors hover:text-white"
                >
                  {siteConfig.lspName}
                </a>
              </p>
            ) : null}
            <p>
              © {new Date().getFullYear()} {siteConfig.companyName} — All Rights Reserved.
            </p>
          </div>
          <p className="leading-4 lg:max-w-[520px] lg:text-right">
            "Reserve Bank of India neither accepts any responsibility nor
            guarantees the present position as to the financial soundness of
            the company or for the correctness of any of the statements or
            representations made or opinions expressed by the company and
            for discharge of any liability by the company."
          </p>
        </div>
      </div>
    </footer>
  );
}
